import * as React from 'react'
import { browserHistory, RouteComponentProps, InjectedRouter, PlainRoute } from 'react-router'
import { inject, observer } from 'mobx-react'
import Helmet from 'react-helmet'
import { WeedmapApi } from '@getgreenline/weedmaps'
import { Tabs, Col, message, Tag } from 'antd'
import groupBy from 'lodash.groupby'
import { AppView, AppMainView } from '../../../../components/AppView'
import AppHeader from '../../../../components/AppView/Header'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { Routes } from '../../../../constants/Routes'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { WeedmapsSettings } from './WeedmapsSettings'
import { IntegrationStore, Integrators, IntegrationLocAPIKey } from '../IntegrationStore'
import { WeedmapsCategories } from './WeedmapsCategories'
import { COLORS } from '../../../../constants/Colors'
import { WeedmapsSync } from './WeedmapsSync'
import { HelpLinks } from '../../../../constants/ExternalLinks'

const { TabPane } = Tabs

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

// eslint-disable-next-line @typescript-eslint/ban-types
interface Props extends RouteComponentProps<{}, {}> {
  router: InjectedRouter
  route: PlainRoute
}

@inject('currentCompanyStore')
@observer
export class DashboardWeedmaps extends React.Component<Props & InjectedProps> {
  integrationStore = new IntegrationStore(Integrators.WEEDMAP)

  company = this.props.currentCompanyStore!.company!

  categories = this.props.currentCompanyStore!.productStore!.flattenedCategories

  componentDidMount = async () => {
    await this.loadWeedmapLocationApiKeys()
    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)
  }

  get tabs() {
    const categoriesCount = this.integrationStore.integrationCategories.filter(
      (category) => category.integrationCategory !== null,
    ).length

    return [
      <TabPane
        className='px-3 pb-3'
        key='1'
        tab={
          <div>
            Categories{' '}
            <Tag className='ml-1 mr-1' color='grey'>
              {categoriesCount}
            </Tag>
          </div>
        }
      >
        <WeedmapsCategories integrationStore={this.integrationStore} />
      </TabPane>,
      <TabPane className='px-3 pb-3' tab='API Settings' key='3'>
        <WeedmapsSettings
          loadWeedmapLocationApiKeys={this.loadWeedmapLocationApiKeys}
          integrationStore={this.integrationStore}
        />
      </TabPane>,
      <TabPane
        className='px-3 pb-3'
        tab='Sync'
        key='2'
        disabled={this.integrationStore.hasNoApiKey}
      >
        <WeedmapsSync integrationStore={this.integrationStore} />
      </TabPane>,
    ]
  }

  loadWeedmapLocationApiKeys = async () => {
    try {
      const locationApiKeys = await WeedmapApi.getLocations(
        this.company.id,
        localStorage.getItem('authToken') || '',
      )
      const locationApiKeysMap = groupBy(locationApiKeys, 'locationId')
      const mappedLocationApiKeys = this.props.currentCompanyStore!.locations!.map((location) => {
        const apiKey = locationApiKeysMap[location.id]
          ? locationApiKeysMap[location.id][0].wmid
          : null
        const apiKeyString = apiKey === null ? null : apiKey.toString()
        return new IntegrationLocAPIKey<Integrators.WEEDMAP>(location, apiKeyString)
      })
      this.integrationStore.setLocationApiKeys(mappedLocationApiKeys)
    } catch (err) {
      message.error('Unable to load weedmap api keys')
    }
  }

  routerWillLeave = () => {
    const { updatableCatagories, updatableApiKeys } = this.integrationStore

    if (updatableCatagories.length > 0) {
      return 'Categories are not saved! Are you sure you want to leave?'
    }

    if (updatableApiKeys.length > 0) {
      return 'API keys are not saved! Are you sure you want to leave?'
    }
  }

  render() {
    return (
      <AppView column>
        <Helmet>
          <title>Integrations | Weedmaps</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader
          title='Weedmaps'
          onBackClick={() => browserHistory.push(Routes.DASHBOARD_INTEGRATIONS(this.company.id))}
          backVisible
        >
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={[
                {
                  href: HelpLinks.WEEDMAPS,
                  title: 'Weedmaps',
                },
              ]}
            />
          </span>
        </AppHeader>
        <AppMainView className='background-grey pb-2'>
          <Col xxl={15} xl={20} lg={24} md={24} sm={24} xs={24}>
            <Tabs
              type='card'
              className='background-white'
              tabBarStyle={{ background: COLORS.backgroundGrey }}
            >
              {this.tabs}
            </Tabs>
          </Col>
        </AppMainView>
      </AppView>
    )
  }
}
